import m from "mithril";
import { getMithril, getUserData } from "../utils.js";
import toast, { showConfirmation } from "../js/toaster.js";

const CoordinatorSetoran = {
    coordinators: [],
    coordinatorBalances: {},
    loading: false,
    error: null,
    subunitId: null,
    userSubunit: null,

    oninit: function () {
        this.loadUserSubunitAndCoordinators();
    },

    // Enhanced user subunit loading with backend API
    loadUserSubunitAndCoordinators: async function () {
        try {
            const userData = await getUserData(true); // Force refresh to get current session
            if (userData) {
                // Check access control
                if (userData.role !== 'manajer' && userData.role !== 'superuser' && userData.role !== 'kasir') {
                    m.route.set('/unauthorized');
                    return;
                }

                // Set user subunit for form data
                this.userSubunit = userData.subunit_id ? {
                    id: userData.subunit_id,
                    nama: userData.subunit_name || 'Subunit'
                } : null;

                // Get fresh subunit ID from backend API
                await this.getCurrentUserSubunitId();
                await this.loadCoordinators();
            } else {
                this.error = 'Gagal memuat data pengguna';
                toast.error(this.error);
            }
        } catch (error) {
            console.error('Error loading user subunit:', error);
            this.error = 'Gagal inisialisasi: ' + error.message;
            toast.error(this.error);
        }
    },

    // Backend API subunit fetching with intelligent caching
    setCachedUserSubunitId: function (subunitId) {
        this._cachedUserSubunit = subunitId;
    },

    getCachedUserSubunitId: function () {
        return this._cachedUserSubunit;
    },

    async getCurrentUserSubunitId() {
        try {
            const response = await getMithril('/api/user/subunit', { component: this });
            if (response.success && response.data) {
                this.subunitId = response.data.id;
                this.setCachedUserSubunitId(response.data.id);
                return response.data.id;
            }
            return null;
        } catch (error) {
            console.error("Failed to fetch user subunit:", error);
            // Fallback to cached data if available
            const cached = this.getCachedUserSubunitId();
            if (cached) {
                this.subunitId = cached;
                return cached;
            }
            return null;
        }
    },

    loadCoordinators: async function () {
        this.loading = true;
        this.error = null;
        m.redraw();

        try {
            // Ensure we have fresh subunit ID
            if (!this.subunitId) {
                await this.getCurrentUserSubunitId();
            }

            // Use getMithril for session-based authentication
            const officersResult = await getMithril('/api/officers', { component: this });

            if (officersResult.success) {
                this.coordinators = officersResult.data.filter(officer => {
                    const chair = officer.chair?.toLowerCase();
                    return parseInt(officer.subunitId) === parseInt(this.subunitId) && 
                           (chair === 'koordinator' || chair === 'kasir');
                });

                // Load balances for each coordinator
                await this.loadCoordinatorsBalances();
                // Load banking history
                await this.loadBankingHistory();
            } else {
                this.error = officersResult.error || 'Gagal memuat koordinator';
                toast.error(this.error);
            }
        } catch (error) {
            console.error('Error loading coordinators:', error);
            this.error = 'Gagal memuat koordinator: ' + error.message;
            toast.error(this.error);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    loadBankingHistory: async function () {
        this.bankingHistoryLoading = true;
        m.redraw();
        try {
            // Use getMithril for session-based authentication
            const response = await getMithril('/api/coordinator-management/banking-history', { component: this });
            if (response.success) {
                this.bankingHistory = response.data;
            } else {
                console.error("Failed to load banking history:", response.error);
            }
        } catch (error) {
            console.error("Error loading banking history:", error);
        } finally {
            this.bankingHistoryLoading = false;
            m.redraw();
        }
    },

    loadCoordinatorsBalances: async function () {
        try {
            // Use getMithril for session-based authentication
            const balanceResult = await getMithril('/api/coordinator-management/balances', { component: this });

            if (balanceResult.success) {
                // Create a map of officer_id to balance
                this.coordinatorBalances = {};
                balanceResult.data.forEach(item => {
                    this.coordinatorBalances[item.officer_id] = parseFloat(item.balance) || 0;
                });
            } else {
                console.warn('Failed to load coordinator balances:', balanceResult.error);
                // Set all balances to 0 if we can't load them
                this.coordinators.forEach(coordinator => {
                    this.coordinatorBalances[coordinator.id] = 0;
                });
            }
        } catch (error) {
            console.error('Error loading coordinator balances:', error);
            // Set all balances to 0 if we can't load them
            this.coordinators.forEach(coordinator => {
                this.coordinatorBalances[coordinator.id] = 0;
            });
        }
    },



    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    },

    // Export coordinator balances to CSV
    exportToExcel: function () {
        if (!this.coordinators || this.coordinators.length === 0) {
            toast.error("Tidak ada data untuk diekspor");
            return;
        }

        const headers = ["Nama Petugas", "Kode", "Subunit", "Dana Dikelola", "Status"];
        // Numbers must be unquoted in CSV so Excel parses them as numbers (not text).
        const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csvNum = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '0';
        };
        const rows = this.coordinators.map(c => {
            const balance = this.coordinatorBalances[c.id] || 0;
            const status = balance > 0 ? "Siap Disetor" : "Kosong";
            return [csvText(c.name), csvText(c.code), csvText(this.userSubunit?.nama || '-'), csvNum(balance), csvText(status)].join(',');
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `Laporan_Setoran_Petugas_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    // Updated banking function to accept description
    bankCollectedFunds: async function (coordinator, description) {
        const balance = this.coordinatorBalances[coordinator.id] || 0;
        if (balance <= 0) return;

        if (!confirm(`Konfirmasi setor dana sebesar ${this.formatCurrency(balance)} untuk koordinator ${coordinator.name}?`)) {
            return;
        }

        this.loading = true;
        m.redraw();

        try {
            const result = await getMithril('/api/coordinator-management/bank-funds', {
                method: 'POST',
                body: {
                    officerId: coordinator.id,
                    amount: balance,
                    description: description
                },
                component: this
            });

            if (result.success) {
                toast.success('Berhasil menyetor dana ke kantor');
                // Refresh balances
                await this.loadCoordinatorsBalances();
                // Refresh history
                await this.loadBankingHistory();

                // Close expanded section if open
                if (this.selectedCoordinator === coordinator.id) {
                    this.selectedCoordinator = null;
                }
            } else {
                toast.error(result.error || 'Gagal menyetor dana');
            }
        } catch (error) {
            console.error('Error banking funds:', error);
            toast.error('Terjadi kesalahan: ' + error.message);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    view: function () {
        const totalHeldFunds = Object.values(this.coordinatorBalances).reduce((sum, balance) => sum + (balance || 0), 0);
        const { coordinators } = this;

        return m("div", { class: "max-w-7xl mx-auto p-6" }, [
            m("div", { class: "mb-8" }, [
                m("div", { class: "flex items-center justify-between mb-2" }, [
                    m("div", { class: "flex items-center space-x-4" }, [
                        m("div", { class: "w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg" }, [
                            m("i", { class: "ri-bank-line text-white text-xl" })
                        ]),
                        m("div", [
                            m("h1", { class: "text-3xl font-bold text-gray-900 dark:text-white" }, "Setoran Petugas"),
                            m("p", { class: "text-lg text-gray-600 dark:text-gray-400" }, "Kelola penyetoran dana yang dikelola oleh petugas (Koordinator/Kasir) ke bank")
                        ])
                    ]),
                    // Global Export Button
                    m("button", {
                        onclick: () => this.exportToExcel(),
                        class: "inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    }, [
                        m("i", { class: "ri-file-excel-2-line mr-2" }),
                        "Export Data"
                    ])
                ]),
                m("div", { class: "h-1 w-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full" })
            ]),

            // Stats Cards
            m("div", { class: "mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" }, [
                m("div", { class: "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-6 border border-blue-200 dark:border-blue-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-blue-600 dark:text-blue-400" }, "Total Dana Dikelola"),
                            m("p", { class: "text-2xl font-bold text-blue-900 dark:text-blue-100" }, this.formatCurrency(totalHeldFunds))
                        ]),
                        m("div", { class: "w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-money-dollar-circle-line text-white text-xl" })
                        ])
                    ])
                ]),
                m("div", { class: "bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-6 border border-green-200 dark:border-green-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-green-600 dark:text-blue-400" }, "Petugas Aktif"),
                            m("p", { class: "text-2xl font-bold text-green-900 dark:text-blue-100" }, this.coordinators.length)
                        ]),
                        m("div", { class: "w-12 h-12 bg-green-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-user-star-line text-white text-xl" })
                        ])
                    ])
                ]),
                m("div", { class: "bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 rounded-xl p-6 border border-orange-200 dark:border-orange-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-orange-600 dark:text-orange-400" }, "Siap Disetor"),
                            m("p", { class: "text-2xl font-bold text-orange-900 dark:text-orange-100" },
                                this.coordinators.filter(c => (this.coordinatorBalances[c.id] || 0) > 0).length
                            )
                        ]),
                        m("div", { class: "w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-bank-line text-white text-xl" })
                        ])
                    ])
                ])
            ]),

            // Error message
            this.error && m("div", { class: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" }, this.error),

            // Coordinators List (Accordion)
            m("div", { class: "bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-8" }, [
                m("div", { class: "p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" }, [
                    m("h2", { class: "text-lg font-bold text-gray-900 dark:text-white" }, "Daftar Petugas")
                ]),
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gradient-to-r from-green-50 to-green-100 dark:from-gray-700 dark:to-gray-600" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-green-800 dark:text-green-300 uppercase tracking-wider" }, "Nama Petugas"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-green-800 dark:text-green-300 uppercase tracking-wider" }, "Kode"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-green-800 dark:text-green-300 uppercase tracking-wider" }, "Dana Dikelola"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-green-800 dark:text-green-300 uppercase tracking-wider" }, "Aksi")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.loading && !this.coordinators.length && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "4" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-3" }, [
                                        m("i", { class: "ri-loader-4-line animate-spin text-3xl text-green-600" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Memuat data petugas...")
                                    ])
                                ])
                            ]),
                            !this.loading && this.coordinators.length === 0 && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "4" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-2" }, [
                                        m("i", { class: "ri-user-star-line text-4xl text-gray-400" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Tidak ada petugas ditemukan")
                                    ])
                                ])
                            ]),
                            this.coordinators.reduce((acc, coordinator, index) => {
                                const balance = this.coordinatorBalances[coordinator.id] || 0;
                                const hasFunds = balance > 0;
                                const isExpanded = this.selectedCoordinator === coordinator.id;

                                acc.push(m("tr", {
                                    key: coordinator.id,
                                    class: index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-750"
                                }, [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white" }, [
                                        m("div", { class: "flex items-center space-x-3" }, [
                                            m("div", {
                                                class: `w-8 h-8 rounded-full flex items-center justify-center ${hasFunds ? 'bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-700'
                                                    }`
                                            }, [
                                                m("i", {
                                                    class: `text-sm ${hasFunds ? 'ri-user-star-line text-green-600 dark:text-green-400' : 'ri-user-line text-gray-500 dark:text-gray-400'
                                                        }`
                                                })
                                            ]),
                                            m("span", coordinator.name)
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                        m("span", {
                                            class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${hasFunds ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                                }`
                                        }, coordinator.code)
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-bold" }, [
                                        m("div", { class: "flex items-center space-x-2" }, [
                                            m("span", {
                                                class: hasFunds ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                            }, this.formatCurrency(balance)),
                                            hasFunds && m("i", { class: "ri-money-dollar-circle-line text-green-500" })
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("button", {
                                            onclick: () => this.selectedCoordinator = isExpanded ? null : coordinator.id,
                                            class: `inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${hasFunds || isExpanded
                                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                }`,
                                            disabled: !hasFunds && !isExpanded
                                        }, [
                                            m("i", { class: `ri-${isExpanded ? 'arrow-up-s' : 'arrow-down-s'}-line mr-2` }),
                                            isExpanded ? "Tutup" : "Proses"
                                        ])
                                    ])
                                ]));

                                if (isExpanded) {
                                    acc.push(m("tr", { key: `expanded-${coordinator.id}` }, [
                                        m("td", { colspan: "4", class: "px-0 py-0" }, [
                                            m("div", { class: "bg-gray-50 dark:bg-gray-900 p-6 border-y border-gray-200 dark:border-gray-700 shadow-inner" }, [
                                                m("div", { class: "max-w-xl mx-auto bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700" }, [
                                                    m("h3", { class: "text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center" }, [
                                                        m("i", { class: "ri-bank-card-line mr-2 text-green-600" }),
                                                        "Setor Dana ke Kantor"
                                                    ]),

                                                    m("div", { class: "mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg flex justify-between items-center" }, [
                                                        m("span", { class: "text-sm text-gray-600 dark:text-gray-400" }, "Total Siap Disetor:"),
                                                        m("span", { class: "text-xl font-bold text-green-600 dark:text-green-400" }, this.formatCurrency(balance))
                                                    ]),

                                                    m("div", { class: "space-y-4" }, [
                                                        m("div", [
                                                            m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Catatan / Keterangan (Opsional)"),
                                                            m("textarea", {
                                                                class: "w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white",
                                                                rows: 3,
                                                                placeholder: "Contoh: Setoran tunai hasil penagihan minggu ini...",
                                                                oninput: (e) => this.inputDescription = e.target.value,
                                                                value: this.inputDescription || ''
                                                            })
                                                        ]),

                                                        m("div", { class: "flex justify-end space-x-3 pt-4" }, [
                                                            m("button", {
                                                                onclick: () => this.selectedCoordinator = null,
                                                                class: "px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                                            }, "Batal"),
                                                            m("button", {
                                                                onclick: () => this.bankCollectedFunds(coordinator, this.inputDescription),
                                                                disabled: this.loading,
                                                                class: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-all duration-200"
                                                            }, [
                                                                this.loading && m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                                                "Konfirmasi Setoran"
                                                            ])
                                                        ])
                                                    ])
                                                ])
                                            ])
                                        ])
                                    ]));
                                }

                                return acc;
                            }, [])
                        ])
                    ])
                ])
            ]),

            // Banking History Table
            m("div", { class: "bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" }, [
                    m("div", [
                        m("h2", { class: "text-lg font-bold text-gray-900 dark:text-white" }, "Riwayat Setoran"),
                        m("p", { class: "text-sm text-gray-500 dark:text-gray-400" }, "Daftar riwayat penyetoran dana dari petugas")
                    ])
                ]),
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Tanggal"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Petugas"),
                                m("th", { class: "px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Jumlah"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Keterangan")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.bankingHistoryLoading && m("tr", [
                                m("td", { colspan: "4", class: "px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" }, [
                                    m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                    "Memuat riwayat..."
                                ])
                            ]),
                            !this.bankingHistoryLoading && (!this.bankingHistory || this.bankingHistory.length === 0) && m("tr", [
                                m("td", { colspan: "4", class: "px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" }, "Belum ada riwayat setoran")
                            ]),
                            this.bankingHistory && this.bankingHistory.map(entry => {
                                return m("tr", [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" },
                                        new Date(entry.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    ),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" }, entry.officer_name),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-green-600 dark:text-green-400" },
                                        this.formatCurrency(Math.abs(parseFloat(entry.collection_amount)))
                                    ),
                                    m("td", { class: "px-6 py-4 text-sm text-gray-500 dark:text-gray-400" }, entry.description || "-")
                                ]);
                            })
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default CoordinatorSetoran;